import { DefaultRootState } from 'react-redux';
import { AnyAction } from 'redux';

export const SET_FIELD_DATA_AS_IS = 'SET_FIELD_DATA_AS_IS';

export interface Action<T = string, P = unknown> extends AnyAction {
    type: T;
    payload: P;
}

export interface ReducerFunction<IS, PT> {
    (state: IS, action: Action<keyof PT, PT[keyof PT]>): IS;
}

type UnknownPayloadTypeMap = Record<string, Record<string, unknown>>;

type ReducerMap<IS = unknown, PT = UnknownPayloadTypeMap> = {
    [key in keyof PT]: (state: IS, action: Action<key, PT[key]>) => IS;
};

export interface Reducer {
    <IS, PT>(initialState: IS, reducerMap: ReducerMap<IS, PT>): ReducerFunction<IS, PT>;
}

interface SetFieldDataAsIsPayload<IS> {
    [SET_FIELD_DATA_AS_IS]: Record<string, IS>;
}

interface AutoGeneratedReducer {
    <IS>(initialState: IS, name: string): ReducerFunction<IS, SetFieldDataAsIsPayload<IS>>;
}

/* eslint-disable etc/no-misused-generics */
export const ActionCreatorHelper =
    <PT>() =>
    <AT extends keyof PT>(type: AT) =>
    (payload: PT[AT]): Action<AT, PT[AT]> => ({
        type,
        payload,
    });
/* eslint-enable etc/no-misused-generics */

const createReducer: Reducer = (initiateState, reducerMap) => (state, action) => {
    const currentState = state === undefined ? initiateState : state;
    if (reducerMap && reducerMap[action.type]) {
        return reducerMap[action.type](currentState, action);
    }
    return currentState;
};

export const createAutoGeneratedReducer: AutoGeneratedReducer = (initialState, name) =>
    createReducer(initialState, {
        [SET_FIELD_DATA_AS_IS]: (state, { payload }) => (payload.hasOwnProperty(name) ? payload[name] : state),
    });

interface MakeSetStoreFieldAction extends Action {
    type: typeof SET_FIELD_DATA_AS_IS;
    source: string;
}

export const makeSetStoreField =
    <K extends keyof DefaultRootState>(fieldName: K) =>
    (payload: DefaultRootState[K]): MakeSetStoreFieldAction => ({
        type: SET_FIELD_DATA_AS_IS,
        source: 'makeSetStoreField',
        payload: {
            [fieldName]: payload,
        },
    });

export default createReducer;
